import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiError, downloadExcel, downloadPdf, downloadAttestation } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  FORMATION_CATEGORIES, FORMATION_FONCTIONS, FORMATION_THEMES, FORMATION_THEMES_MEDCOORD,
  FORMATION_BY_THEME, FORMATION_ORGANISMES, FORMATION_OBLIGATIONS,
} from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  GraduationCap, Plus, FileSpreadsheet, FileText, Pencil, Trash2, Search,
  Clock, Users, CheckCircle2, Award, BookOpen, CalendarDays, Building, BadgeCheck,
  FileDown, MailWarning,
} from "lucide-react";

const EMPTY = {
  nom: "", prenom: "", fonction: "", categorie: "Soins AS_INFI", theme: "",
  formation: "", organisme: "", date_formation: "", reussite: false,
  attestation_recue: false, heures_validees: 0, notes: "",
};

function StatCard({ icon: Icon, label, value, sub, gradient, delay }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay }}
      className="relative overflow-hidden rounded-2xl card-3d p-5" style={{ background: gradient }} data-testid={`formation-stat-${label}`}>
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/10 blur-xl" />
      <Icon className="h-6 w-6 text-white/90" />
      <div className="mt-3 text-3xl font-display font-extrabold text-white leading-none">{value}</div>
      <div className="text-sm text-white/85 mt-1 font-medium">{label}</div>
      {sub && <div className="text-xs text-white/70 mt-0.5">{sub}</div>}
    </motion.div>
  );
}

function Initials({ name }) {
  const ini = (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0"
      style={{ background: "linear-gradient(135deg,#a855f7,#6366f1)" }}>{ini}</div>
  );
}

function ProgressRow({ e, i, year }) {
  const ob = FORMATION_OBLIGATIONS[e.categorie] || { color: "#94a3b8", text: "" };
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.3) }}
      className="glass rounded-2xl p-4 sm:p-5" data-testid={`suivi-row-${i}`}>
      <div className="flex items-center gap-3">
        <Initials name={e.name} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold leading-tight truncate">{e.name || "—"}</h3>
            {e.fonction && <span className="text-[11px] rounded-full px-2 py-0.5 bg-white/8 text-foreground/70">{e.fonction}</span>}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{e.categorie} · {ob.text}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-extrabold text-lg leading-none" style={{ color: e.complete ? "#34d399" : ob.color }}>
            {e.heures}<span className="text-sm text-muted-foreground font-semibold"> / {e.target}h</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{e.count} formation(s) · {e.themes} thème(s)</div>
        </div>
      </div>
      <div className="mt-3 h-2.5 rounded-full bg-white/8 overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${e.progress}%` }} transition={{ duration: 0.7, ease: "easeOut" }}
          className="h-full rounded-full" style={{ background: e.complete ? "linear-gradient(90deg,#10b981,#34d399)" : `linear-gradient(90deg,${ob.color},${ob.color}aa)` }} />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[11px] text-muted-foreground">{e.progress}% de l'objectif annuel</span>
        <div className="flex items-center gap-2">
          {e.complete
            ? <span className="text-[11px] font-semibold text-emerald-400 flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" /> Conforme</span>
            : <span className="text-[11px] font-semibold text-amber-400">{Math.max(e.target - e.heures, 0).toFixed(1)}h restantes</span>}
          <button onClick={() => downloadAttestation(e.nom, e.prenom, year)} data-testid={`attestation-${i}`}
            className="text-[11px] font-semibold text-violet-300 hover:text-violet-200 flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:bg-white/5 transition" title="Télécharger l'attestation PDF">
            <FileDown className="h-3.5 w-3.5" /> Attestation
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function AnalysisBar({ item, max, color }) {
  const pct = max ? Math.round((item.value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm"><span className="truncate pr-3 text-foreground/85">{item.name}</span><span className="font-semibold shrink-0">{item.value}</span></div>
      <div className="h-2 rounded-full bg-white/8 overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} className="h-full rounded-full" style={{ background: color }} />
      </div>
    </div>
  );
}

export default function Formation() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [stats, setStats] = useState({ years: [], summary: {}, employees: [], by_theme: [], by_category: [] });
  const [year, setYear] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");

  const loadList = async () => { const { data } = await api.get("/formations"); setList(data); };
  const loadStats = async () => { const { data } = await api.get("/formations/stats", { params: year ? { year } : {} }); setStats(data); };
  useEffect(() => { loadList(); }, []);
  useEffect(() => { loadStats(); /* eslint-disable-next-line */ }, [year]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };

  const save = async () => {
    if (!form.nom.trim()) return toast.error("Le nom est requis");
    if (!form.formation.trim()) return toast.error("L'intitulé de la formation est requis");
    try {
      const payload = { ...form, heures_validees: parseFloat(form.heures_validees) || 0 };
      if (editing) await api.put(`/formations/${editing.id}`, payload);
      else await api.post("/formations", payload);
      setOpen(false);
      toast.success(editing ? "Formation mise à jour" : "Formation encodée");
      loadList(); loadStats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (it) => {
    try { await api.delete(`/formations/${it.id}`); toast.success("Supprimé"); loadList(); loadStats(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendAlert = async () => {
    try {
      const { data } = await api.post("/reminders/formations", {}, { params: year ? { year } : {} });
      if (data.sent?.length) toast.success(`Alerte envoyée à ${data.sent.length} destinataire(s) — ${data.count} non conforme(s).`);
      else toast.warning(`${data.count} non conforme(s). Aucun e-mail envoyé (vérifiez les adresses ou le domaine Resend).`);
      if (data.errors?.length) toast.error(`${data.errors.length} envoi(s) en échec.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = list.filter((f) => {
    if (catFilter !== "all" && f.categorie !== catFilter) return false;
    if (year && (f.date_formation || "").slice(0, 4) !== year) return false;
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return [f.nom, f.prenom, f.formation, f.theme, f.organisme, f.fonction].some((v) => (v || "").toLowerCase().includes(t));
  });

  const s = stats.summary || {};
  const maxTheme = Math.max(1, ...stats.by_theme.map((x) => x.value));
  const maxCat = Math.max(1, ...stats.by_category.map((x) => x.value));

  const isMedCoord = form.categorie === "Médecin coordinateur";
  const themeOptions = isMedCoord ? FORMATION_THEMES_MEDCOORD : FORMATION_THEMES;
  let formationOptions = FORMATION_BY_THEME[form.theme] || [];
  if (form.formation && !formationOptions.includes(form.formation)) formationOptions = [form.formation, ...formationOptions];

  return (
    <div className="p-5 sm:p-8 max-w-[1280px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div data-tour="form-header">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-violet-300">Ressources humaines · IrisCare</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1 flex items-center gap-3">
            <span className="h-11 w-11 rounded-2xl flex items-center justify-center bg-violet-500/20 text-violet-300"><GraduationCap className="h-6 w-6" /></span>
            Formation du personnel
          </h1>
          <p className="text-muted-foreground mt-1">Encodage et suivi des obligations de formation permanente (Bruxelles).</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={year || "all"} onValueChange={(v) => setYear(v === "all" ? "" : v)}>
            <SelectTrigger className="h-11 w-[150px]" data-testid="formation-year" data-tour="form-year"><CalendarDays className="h-4 w-4 mr-1.5" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les années</SelectItem>
              {stats.years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" className="h-11" onClick={() => downloadExcel("formations")} data-testid="formation-export-xlsx" data-tour="form-export"><FileSpreadsheet className="h-4 w-4 mr-2" /> Excel</Button>
          <Button variant="outline" className="h-11" onClick={() => downloadPdf("formations")} data-testid="formation-export-pdf"><FileText className="h-4 w-4 mr-2" /> PDF</Button>
          {user?.role === "admin" && (
            <Button variant="outline" className="h-11 border-amber-500/40 text-amber-300 hover:bg-amber-500/10" onClick={sendAlert} data-testid="formation-alert-btn"><MailWarning className="h-4 w-4 mr-2" /> Alerte non-conformité</Button>
          )}
          <Button className="h-11 bg-violet-600 hover:bg-violet-500" onClick={openNew} data-testid="formation-add-btn" data-tour="form-add"><Plus className="h-4 w-4 mr-2" /> Encoder une formation</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6" data-tour="form-stats">
        <StatCard icon={BookOpen} label="Formations encodées" value={s.entries ?? 0} gradient="linear-gradient(135deg,#7c3aed,#a855f7)" delay={0.02} />
        <StatCard icon={Clock} label="Heures validées" value={s.heures ?? 0} sub="cumul sur la période" gradient="linear-gradient(135deg,#0ea5e9,#06b6d4)" delay={0.06} />
        <StatCard icon={Users} label="Employés formés" value={s.employes ?? 0} gradient="linear-gradient(135deg,#f59e0b,#f97316)" delay={0.1} />
        <StatCard icon={CheckCircle2} label="Personnel conforme" value={`${s.conformes ?? 0}/${s.employes ?? 0}`} sub={`${s.attestations_pct ?? 0}% attestations reçues`} gradient="linear-gradient(135deg,#059669,#10b981)" delay={0.14} />
      </div>

      <Tabs defaultValue="suivi" className="mt-7">
        <TabsList className="glass" data-testid="formation-tabs" data-tour="form-tabs">
          <TabsTrigger value="suivi" data-testid="tab-suivi"><BadgeCheck className="h-4 w-4 mr-1.5" /> Suivi par employé</TabsTrigger>
          <TabsTrigger value="registre" data-testid="tab-registre"><BookOpen className="h-4 w-4 mr-1.5" /> Registre</TabsTrigger>
          <TabsTrigger value="analyse" data-testid="tab-analyse"><Award className="h-4 w-4 mr-1.5" /> Analyse</TabsTrigger>
        </TabsList>

        {/* SUIVI */}
        <TabsContent value="suivi" className="mt-5">
          {stats.employees.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center text-muted-foreground">Aucune formation encodée pour cette période.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {stats.employees.map((e, i) => <ProgressRow key={e.name + i} e={e} i={i} year={year} />)}
            </div>
          )}
        </TabsContent>

        {/* REGISTRE */}
        <TabsContent value="registre" className="mt-5">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un nom, une formation, un thème…" className="pl-10 h-11" data-testid="formation-search" />
            </div>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="h-11 sm:w-[220px]" data-testid="formation-cat-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les catégories</SelectItem>
                {FORMATION_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {filtered.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center text-muted-foreground">Aucune formation trouvée.</div>
          ) : (
            <div className="space-y-2.5">
              {filtered.map((f, i) => {
                const ob = FORMATION_OBLIGATIONS[f.categorie] || { color: "#a855f7" };
                return (
                  <motion.div key={f.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: Math.min(i * 0.02, 0.25) }}
                    className="glass rounded-2xl p-4 flex items-center gap-3 sm:gap-4" data-testid={`formation-item-${f.id}`}>
                    <Initials name={`${f.nom} ${f.prenom}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-bold leading-tight truncate">{f.nom} {f.prenom}</h3>
                        {f.fonction && <span className="text-[11px] rounded-full px-2 py-0.5 bg-white/8 text-foreground/70">{f.fonction}</span>}
                        <span className="text-[11px] rounded-full px-2 py-0.5" style={{ background: ob.color + "22", color: ob.color }}>{f.categorie}</span>
                      </div>
                      <p className="text-sm text-foreground/90 mt-0.5 truncate">{f.formation}{f.theme ? <span className="text-muted-foreground"> · {f.theme}</span> : null}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                        {f.date_formation && <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {f.date_formation}</span>}
                        {f.organisme && <span className="flex items-center gap-1"><Building className="h-3 w-3" /> {f.organisme}</span>}
                        <span className="flex items-center gap-1 font-semibold text-foreground/80"><Clock className="h-3 w-3" /> {f.heures_validees}h</span>
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <div className="flex gap-1.5">
                        {f.reussite && <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-emerald-500/20 text-emerald-300">Réussie</span>}
                        {f.attestation_recue && <span className="text-[11px] font-semibold rounded-full px-2 py-0.5 bg-sky-500/20 text-sky-300">Attestation</span>}
                      </div>
                      <div className="flex gap-1 no-print">
                        <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/10" onClick={() => openEdit(f)} data-testid={`formation-edit-${f.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:bg-red-500/10" onClick={() => remove(f)} data-testid={`formation-del-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ANALYSE */}
        <TabsContent value="analyse" className="mt-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="glass rounded-2xl card-3d p-5">
              <h2 className="font-display font-bold flex items-center gap-2 mb-4"><BookOpen className="h-5 w-5 text-violet-400" /> Répartition par thème</h2>
              <div className="space-y-3">
                {stats.by_theme.length === 0 && <p className="text-sm text-muted-foreground">Aucune donnée.</p>}
                {stats.by_theme.map((t) => <AnalysisBar key={t.name} item={t} max={maxTheme} color="linear-gradient(90deg,#a855f7,#6366f1)" />)}
              </div>
            </div>
            <div className="glass rounded-2xl card-3d p-5">
              <h2 className="font-display font-bold flex items-center gap-2 mb-4"><Clock className="h-5 w-5 text-cyan-400" /> Heures par catégorie</h2>
              <div className="space-y-3">
                {stats.by_category.length === 0 && <p className="text-sm text-muted-foreground">Aucune donnée.</p>}
                {stats.by_category.map((c) => <AnalysisBar key={c.name} item={c} max={maxCat} color="linear-gradient(90deg,#06b6d4,#0ea5e9)" />)}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Encode dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><GraduationCap className="h-5 w-5 text-violet-400" /> {editing ? "Modifier la formation" : "Encoder une formation"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Formulaire d'encodage d'une formation du personnel</DialogDescription>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v, theme: "", formation: "" })}>
                  <SelectTrigger data-testid="form-categorie"><SelectValue /></SelectTrigger>
                  <SelectContent>{FORMATION_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fonction</Label>
                <Select value={form.fonction} onValueChange={(v) => setForm({ ...form, fonction: v })}>
                  <SelectTrigger data-testid="form-fonction"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{FORMATION_FONCTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input data-testid="form-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Ex : DUPONT" />
              </div>
              <div className="space-y-2">
                <Label>Prénom</Label>
                <Input data-testid="form-prenom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} placeholder="Ex : Marie" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Thème obligatoire</Label>
              <Select value={form.theme} onValueChange={(v) => setForm({ ...form, theme: v, formation: "" })}>
                <SelectTrigger data-testid="form-theme"><SelectValue placeholder="Choisir un thème" /></SelectTrigger>
                <SelectContent>{themeOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Formation suivie</Label>
              <Input data-testid="form-formation" list="formation-suivie-list" value={form.formation}
                onChange={(e) => setForm({ ...form, formation: e.target.value })}
                placeholder={isMedCoord || formationOptions.length === 0 ? "Saisir l'intitulé de la formation" : "Choisir dans la liste ou saisir au clavier"} />
              <datalist id="formation-suivie-list">{formationOptions.map((c) => <option key={c} value={c} />)}</datalist>
              {!isMedCoord && (FORMATION_BY_THEME[form.theme] || []).length > 0 && (
                <p className="text-[11px] text-muted-foreground">Liste proposée pour ce thème — vous pouvez aussi taper une autre formation au clavier.</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Organisme</Label>
                <Input data-testid="form-organisme" list="organismes" value={form.organisme} onChange={(e) => setForm({ ...form, organisme: e.target.value })} placeholder="Ex : E-learning Femarbel" />
                <datalist id="organismes">{FORMATION_ORGANISMES.map((o) => <option key={o} value={o} />)}</datalist>
              </div>
              <div className="space-y-2">
                <Label>Date de la formation</Label>
                <Input type="date" data-testid="form-date" value={form.date_formation || ""} onChange={(e) => setForm({ ...form, date_formation: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div className="space-y-2">
                <Label>Heures validées</Label>
                <Input type="number" min="0" step="0.5" data-testid="form-heures" value={form.heures_validees} onChange={(e) => setForm({ ...form, heures_validees: e.target.value })} />
              </div>
              <div className="flex items-center justify-between glass rounded-xl px-3 h-11">
                <Label className="cursor-pointer">Réussite</Label>
                <Switch checked={form.reussite} onCheckedChange={(v) => setForm({ ...form, reussite: v })} data-testid="form-reussite" />
              </div>
              <div className="flex items-center justify-between glass rounded-xl px-3 h-11">
                <Label className="cursor-pointer">Attestation</Label>
                <Switch checked={form.attestation_recue} onCheckedChange={(v) => setForm({ ...form, attestation_recue: v })} data-testid="form-attestation" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea data-testid="form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Remarques éventuelles" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="bg-violet-600 hover:bg-violet-500" onClick={save} data-testid="form-save">{editing ? "Enregistrer" : "Encoder"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
